import React from 'react';
import { useTranslation } from '../../../i18n';

interface OverallFeedbackCardProps {
  feedback?: string;
  topStrength?: string;
  topStrengthScore?: string;
  biggestGap?: string;
  biggestGapScore?: string;
  deliverySummary?: string;
  deliveryMetric?: string;
}

export const OverallFeedbackCard: React.FC<OverallFeedbackCardProps> = ({
  feedback,
  topStrength,
  topStrengthScore,
  biggestGap,
  biggestGapScore,
  deliverySummary,
  deliveryMetric,
}) => {
  const { t } = useTranslation();
  const sanitizeText = (val?: string | null) => {
    if (!val || typeof val !== 'string') return '';
    return val
      .replace(/\[object Object\]/g, '')
      .replace(/;\s*;\s*/g, '; ')
      .replace(/:\s*;/g, ': ')
      .replace(/Key demonstrated strengths:\s*\./g, '')
      .replace(/Priority coaching focus:\s*\./g, '')
      .trim();
  };

  const cleanFeedback = sanitizeText(feedback);
  const cleanTopStrength = sanitizeText(topStrength);
  const cleanBiggestGap = sanitizeText(biggestGap);
  return (
    <div
      className="scorecard-card"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: 'clamp(14px, 3vw, 22px) clamp(12px, 3vw, 24px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'grid',
        gap: '16px',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: '#F5F3FF',
              color: '#7C3AED',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" />
            </svg>
          </span>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '15.5px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            {t('scorecard.overall_feedback_title', undefined, 'Overall feedback')}
          </h2>
        </div>

        <span
          style={{
            fontSize: '11.5px',
            fontWeight: 600,
            background: '#F5F3FF',
            color: '#6D28D9',
            padding: '3px 10px',
            borderRadius: '999px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <svg style={{ width: '12px', height: '12px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" />
          </svg>
          {t('scorecard.ai_summary_reviewed', undefined, 'AI summary · reviewed by coach')}
        </span>
      </div>

      {/* 2-Column Content */}
      <div className="overall-feedback-grid">
        {/* Left: Narrative */}
        <div style={{ display: 'grid', gap: '10px', minWidth: 0 }}>
          {cleanFeedback ? (
            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--navy)', lineHeight: 1.55, whiteSpace: 'pre-line', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              {cleanFeedback}
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)', fontStyle: 'italic' }}>
              {t('scorecard.overall_feedback_empty', undefined, 'Overall feedback will appear here once the evaluation is complete.')}
            </p>
          )}
          <span style={{ fontSize: '11.5px', color: 'var(--muted)', wordBreak: 'break-word' }}>
            {t('scorecard.based_on_rubric', undefined, "Based on the recording, transcript and your organisation's rubric.")}
          </span>
        </div>

        {/* Right: Key Metric Summary Badges */}
        <div style={{ display: 'grid', gap: '8px', minWidth: 0 }}>
          {/* Top Strength */}
          {cleanTopStrength && (
            <div
              className="overall-feedback-badge"
              style={{
                background: '#E8F5E9',
              }}
            >
              <span className="overall-feedback-badge-tag" style={{ color: '#1B5E20' }}>
                {t('scorecard.top_strength_tag', undefined, 'TOP STRENGTH')}
              </span>
              <span className="overall-feedback-badge-text">
                {cleanTopStrength}
              </span>
              <span className="overall-feedback-badge-score" style={{ color: '#1B5E20' }}>
                {topStrengthScore || ''}
              </span>
            </div>
          )}

          {/* Biggest Gap */}
          {cleanBiggestGap && (
            <div
              className="overall-feedback-badge"
              style={{
                background: '#FFF3E0',
              }}
            >
              <span className="overall-feedback-badge-tag" style={{ color: '#C2410C' }}>
                {t('scorecard.biggest_gap_tag', undefined, 'BIGGEST GAP')}
              </span>
              <span className="overall-feedback-badge-text">
                {cleanBiggestGap}
              </span>
              <span className="overall-feedback-badge-score" style={{ color: '#C2410C' }}>
                {biggestGapScore || ''}
              </span>
            </div>
          )}

          {/* Delivery */}
          {(deliverySummary || deliveryMetric) && (
            <div
              className="overall-feedback-badge"
              style={{
                background: '#EFF6FF',
              }}
            >
              <span className="overall-feedback-badge-tag" style={{ color: '#1D4ED8' }}>
                {t('scorecard.delivery_tag', undefined, 'DELIVERY')}
              </span>
              <span className="overall-feedback-badge-text">
                {deliverySummary || t('scorecard.speaking_pace_measured', undefined, 'Speaking pace measured')}
              </span>
              <span className="overall-feedback-badge-score" style={{ color: '#1D4ED8' }}>
                {deliveryMetric || ''}
              </span>
            </div>
          )}

          {!topStrength && !biggestGap && !deliverySummary && (
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
              {t('scorecard.metric_breakdown_empty', undefined, 'Metric breakdowns will appear after evaluation.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
